<div class="guarantor-component">

  @if (!singleEntry) {
    <div class="row">
      <div class="col-12">
        <h2 class="mat-h2">Additional guarantors</h2>
        <div class="mat-body separator-bottom">Do you need to add a guarantor that wasn't listed on the previous screen?</div>
      </div>
    </div>
  }

  <form [formGroup]="formGroup">
    @for (guarantorFormGroup of guarantorFormGroups(); track guarantorFormGroup; let index = $index) {
      @if (!singleEntry) {
        <div class="row">
          <div class="col-12">
            <div class="application-subsection-title">Additional guarantor {{index + 1}}</div>
          </div>
        </div>
      }
      <div class="row">
        <div class="col-lg-3 col-md-12">
          <mat-button-toggle-group
            [hideSingleSelectionIndicator]="true"
            [hideMultipleSelectionIndicator]="true"
            [formControl]="guarantorFormGroupFormControl(guarantorFormGroup, 'type')">
            <mat-button-toggle value="Entity">Entity</mat-button-toggle>
            <mat-button-toggle value="Individual">Individual</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      </div>

      <!-- ENTITY -->
      @if (guarantorFormGroupFormControl(guarantorFormGroup, 'type').value === 'Entity' &&
        guarantorFormGroup.controls.organisation) {
        <div class="row">
          <div class="col-lg-6 col-md-12">
            <business-number-search
              [mark]="markObservable"
              [searchFn]="businessNumberSearchFn"
              [formControl]="guarantorFormGroupFormControl(guarantorFormGroup, 'organisation')"></business-number-search>
          </div>
          <div class="col-lg-3 col-md-12">
            <entity-type
              [readonly]="entityTypeReadonly"
              [mark]="markObservable"
              [formControl]="guarantorFormGroupFormControl(guarantorFormGroup, 'organisationType')">
            </entity-type>
          </div>
        </div>

        <!-- A trust or partnership has no directors or shareholders for the company check to
             return, so it collects its trustees/partners instead - the same substitution the
             applicant's own trust makes on the Key People step. They hang off the guarantor entity,
             which is persisted as soon as its type is known, so the section appears without the
             broker having to ask for it. -->
        @if (kybEnabled && partiesEntityType(guarantorFormGroup); as partiesEntityType) {
          @if (entityFor(guarantorFormGroup); as guarantorEntity) {
            <div class="row separator-bottom">
              <div class="col-12">
                <kyb-added-parties
                  [applicationId]="applicationId ?? 0"
                  [parentEntity]="guarantorEntity"
                  [entityType]="partiesEntityType"
                  [businessNumberSearchFn]="businessNumberSearchFn"
                  [kybResult]="kybResult"
                  [kybResultFn]="kybResultFn"
                  [kybRefreshFn]="kybRefreshFn"
                  [kybInitiateCorporatePartyFn]="kybInitiateCorporatePartyFn"
                  [guarantorLockedStakeholderIds]="guarantorLockedStakeholderIds"
                  [mark]="markObservable"
                  (changed)="onPartiesChanged(guarantorFormGroup, $event)"
                  (personRoleChange)="onPersonRoleChange($event)"
                  (personSelectedChange)="onPersonSelectedChange($event)"
                  (manualKybEntry)="startManualKybById($event)"
                  (manualPeopleChange)="onNestedManualPeopleChange($event)"
                  (manualValidityChange)="onNestedManualValidityChange($event)"
                  (resolved)="onKybResolved($event)">
                </kyb-added-parties>
              </div>
            </div>
          } @else {
            <!-- The guarantor entity is still being created, so there is nothing for its
                 trustees/partners to hang off yet. Retry re-sends the initiate, which is the only
                 thing that creates it, so a failed one is not a dead end. -->
            <div class="row separator-bottom">
              <div class="col-12">
                <kyb-status
                  [status]="'pending'"
                  [businessName]="businessNameFor(guarantorFormGroup)"
                  (retry)="retryPartiesEntity(guarantorFormGroup)">
                </kyb-status>
              </div>
            </div>
          }
        } @else if (kybEnabled && !entityStatus(guarantorFormGroup)) {
          <div class="row separator-bottom">
            <div class="col-12">
              <button mat-stroked-button (click)="selectSigners(guarantorFormGroup)">
                <span class="mdi mdi-account-check" matPrefix></span>
                Select signers
              </button>
            </div>
          </div>
        } @else if (kybEnabled) {
          <div class="row separator-bottom">
            <div class="col-12">
              @if (entityFor(guarantorFormGroup); as guarantorEntity) {
                @if (guarantorEntity.mode === 'MANUAL') {
                  <manual-kyb-people
                    [commercialEntityId]="guarantorEntity.commercialEntityId"
                    [initialPeople]="manualPeopleFor(guarantorEntity)"
                    [uploadedDocuments]="uploadedDocuments"
                    [lockRoles]="isSoleTraderGuarantor(guarantorFormGroup, guarantorEntity)"
                    (idDocumentDelete)="idDocumentDelete.emit($event)"
                    (peopleChange)="onManualPeopleChange(guarantorEntity, $event)"
                    (validChange)="onManualValidityChange(guarantorEntity, $event)">
                  </manual-kyb-people>
                } @else if (entityStatus(guarantorFormGroup) === 'resolved') {
                  <kyb-entity-section
                    [entity]="guarantorEntity"
                    [entityType]="signerEntityTypeFor(guarantorFormGroup, guarantorEntity)"
                    [forceShowPeople]="true"
                    [showHeader]="false"
                    [guarantorLockedStakeholderIds]="guarantorLockedStakeholderIds"
                    (personRoleChange)="onPersonRoleChange($event)"
                    (personSelectedChange)="onPersonSelectedChange($event)">
                  </kyb-entity-section>
                } @else {
                  <kyb-status
                    [applicationId]="applicationId"
                    [commercialEntityId]="guarantorEntity.commercialEntityId"
                    [businessName]="businessNameFor(guarantorFormGroup)"
                    [pollFn]="kybResultFn"
                    [refreshFn]="kybRefreshFn"
                    (manualEntry)="startManualKyb(guarantorFormGroup)"
                    (resolved)="onKybResolved($event)">
                  </kyb-status>
                }
              } @else {
                <kyb-status
                  [applicationId]="applicationId"
                  [commercialEntityId]="guarantorFormGroup.controls.commercialEntityId?.value ?? null"
                  [businessName]="businessNameFor(guarantorFormGroup)"
                  [pollFn]="kybResultFn"
                  [refreshFn]="kybRefreshFn"
                  (resolved)="onKybResolved($event)">
                </kyb-status>
              }
            </div>
          </div>
        }
      }

      <!-- INDIVIDUAL -->
      @if (guarantorFormGroupFormControl(guarantorFormGroup, 'type').value === 'Individual' &&
        guarantorFormGroup.controls.firstName) {
        <!-- Shown in the Key People table's shape, so an individual guarantor reads the same way
             wherever the broker meets one. Every tick is fixed: they are a guarantor because they
             were added on this step, and an external individual signs for no entity here. -->
        @if (individualName(guarantorFormGroup); as name) {
          <div class="row">
            <div class="col-12">
              <table class="kyb-people-table">
                <thead>
                  <tr>
                    <th class="col-select"></th>
                    <th class="col-name">Name</th>
                    <th class="col-role">Business Role</th>
                    <th class="col-ownership">Ownership %</th>
                    <th class="col-tick">Guarantor</th>
                    <th class="col-tick">Signer</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="col-select">
                      <kyb-lock-tick [checked]="true" [locked]="true" [tooltip]="addedGuarantorLockTooltip">
                      </kyb-lock-tick>
                    </td>
                    <td class="col-name">{{name}}</td>
                    <td class="col-role">-</td>
                    <td class="col-ownership">-</td>
                    <td class="col-tick">
                      <kyb-lock-tick [checked]="true" [locked]="true" [tooltip]="addedGuarantorLockTooltip">
                      </kyb-lock-tick>
                    </td>
                    <td class="col-tick">
                      <kyb-lock-tick [checked]="false" [locked]="true" [tooltip]="addedGuarantorSignerTooltip">
                      </kyb-lock-tick>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        }

        <div class="row separator-bottom">
          <div class="col-12">
            <button mat-stroked-button (click)="openIndividualDialog(guarantorFormGroup)">
              <span class="mdi mdi-account-plus" matPrefix></span>
              {{guarantorFormGroup.controls.firstName.value ? 'Edit Details' : 'Add Individual'}}
            </button>
          </div>
        </div>
      }

      @if (!singleEntry && canRemoveGuarantor(guarantorFormGroup)) {
        <div class="row separator-bottom">
          <div class="col-12">
            <button mat-stroked-button color="warn" (click)="removeGuarantor(guarantorFormGroup)">Remove guarantor</button>
          </div>
        </div>
      }
    }

    @if (!singleEntry) {
      <div class="row separator-bottom">
        <div class="col-12">
          <button mat-stroked-button (click)="addGuarantor()">
            <span class="mdi mdi-plus" matPrefix></span>
            Add guarantor
          </button>
        </div>
      </div>
    }
  </form>

  @for (error of kybErrors(); track error) {
    <div class="row separator-bottom">
      <div class="col-12">
        <message-box type="danger">{{error}}</message-box>
      </div>
    </div>
  }

</div>
